/**
 * Unit detail drawer for Build pipeline — move phase, BOM, issue, history,
 * and labeled PROPOSED floor overlays (inert until later slices).
 */
import { useEffect, useMemo, useState } from "react";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { canMoveUnitToPhase } from "@/lib/manufacturing/buildPipelinePhaseAccess";
import type { MfgBuildPipelinePhase } from "@/lib/manufacturing/buildPipelineTypes";
import {
  createManufacturingMaterialIssuance,
  listManufacturingMaterialIssuances,
  type MfgMaterialIssuanceSummary,
} from "@/lib/manufacturing/materialIssuanceClient";
import { getManufacturingUnitDetail, type ManufacturingUnitDetail } from "@/lib/manufacturing/productionClient";
import { BuildPipelineUnitShipSection } from "./BuildPipelineUnitShipSection";
import { BuildPipelineUnitPlantPacket } from "./BuildPipelineUnitPlantPacket";
import { getManufacturingRecipeDetail } from "@/lib/manufacturing/recipesClient";
import { listManufacturingMaterials, type MfgMaterial, type MfgUnit } from "@/lib/manufacturingClient";
import { BuildPipelineUnitLoadAssign } from "./BuildPipelineUnitLoadAssign";
import type { MfgProductionLoad } from "@/lib/manufacturing/productionLoadsClient";
import {
  BuildPipelineUnitPhaseHistorySection,
  BuildPipelineUnitProposedFloorSection,
  type BuildPipelineHistoryRow,
} from "./BuildPipelineUnitDetailHistory";

const SALE_LABEL: Record<string, string> = {
  inventory_no_sale_yet: "Stock",
  cash: "Cash",
  finance: "Finance",
  rto: "RTO",
};

export function BuildPipelineUnitDetailDrawer({
  unit,
  phases,
  openLoads,
  actorRoles,
  moveBusy,
  loadBusy,
  onClose,
  onMove,
  onAssignLoad,
  onIssued,
  onShip,
  shipBusy,
}: {
  unit: MfgUnit;
  phases: readonly MfgBuildPipelinePhase[];
  openLoads: MfgProductionLoad[];
  actorRoles: readonly string[];
  moveBusy: boolean;
  loadBusy: boolean;
  shipBusy?: boolean;
  onClose: () => void;
  onMove: (unitId: string, toPhaseId: string, notes?: string) => Promise<void>;
  onAssignLoad: (loadId: string | null) => void;
  onIssued?: () => void;
  onShip?: () => void;
}) {
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState<BuildPipelineHistoryRow[]>([]);
  const [builtStatus, setBuiltStatus] = useState<string | null>(null);
  const [bomLines, setBomLines] = useState<
    Array<{ id: string; label: string; quantity: number; role: string; unit?: string }>
  >([]);
  const [materials, setMaterials] = useState<MfgMaterial[]>([]);
  const [issuances, setIssuances] = useState<MfgMaterialIssuanceSummary[]>([]);
  const [issMat, setIssMat] = useState("");
  const [issQty, setIssQty] = useState("");
  const [issBusy, setIssBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailBusy, setDetailBusy] = useState(true);
  const [plantPacket, setPlantPacket] = useState<
    ManufacturingUnitDetail["plant_packet"]
  >([]);

  const phaseById = useMemo(
    () => Object.fromEntries(phases.map((p) => [p.id, p])),
    [phases],
  );
  const sortedPhases = useMemo(
    () => [...phases].sort((a, b) => a.sequence_index - b.sequence_index),
    [phases],
  );
  const currentIdx = sortedPhases.findIndex((p) => p.id === unit.current_phase_id);
  const fromPhase =
    currentIdx >= 0 ? sortedPhases[currentIdx] : sortedPhases.find((p) => p.is_initial) ?? null;
  const prev = currentIdx > 0 ? sortedPhases[currentIdx - 1] : null;
  const next =
    currentIdx >= 0 && currentIdx < sortedPhases.length - 1 ? sortedPhases[currentIdx + 1] : null;
  const canPrev = prev ? canMoveUnitToPhase(actorRoles, fromPhase, prev) : false;
  const canNext = next ? canMoveUnitToPhase(actorRoles, fromPhase, next) : false;
  const canMove = canPrev || canNext;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setDetailBusy(true);
      setError(null);
      const [detailRes, matsRes, issRes, recipeRes] = await Promise.all([
        getManufacturingUnitDetail(unit.id),
        listManufacturingMaterials(),
        listManufacturingMaterialIssuances({ mfg_unit_id: unit.id }),
        unit.recipe_id
          ? getManufacturingRecipeDetail(unit.recipe_id)
          : Promise.resolve({ ok: false as const, status: 0, error: "skip" }),
      ]);
      if (cancelled) return;
      if (detailRes.ok) {
        setHistory(detailRes.data.detail.history);
        setBuiltStatus(detailRes.data.detail.built_unit?.status ?? null);
        setPlantPacket(detailRes.data.detail.plant_packet ?? []);
      } else if (detailRes.status !== 0) {
        setError(detailRes.error);
      }
      if (matsRes.ok) {
        const all = Array.isArray(matsRes.data.materials) ? matsRes.data.materials : [];
        setMaterials(all.filter((m) => m.plant_id === unit.plant_id));
      }
      if (issRes.ok) {
        setIssuances(Array.isArray(issRes.data.events) ? issRes.data.events : []);
      }
      if (recipeRes.ok) {
        const lines = Array.isArray(recipeRes.data.lines) ? recipeRes.data.lines : [];
        setBomLines(
          lines.map((ln) => ({
            id: ln.id,
            label:
              ln.material_name ||
              ln.material_code ||
              ln.component_recipe_name ||
              ln.component_recipe_code ||
              ln.material_use_name ||
              "Line",
            quantity: ln.quantity,
            role: ln.role || "structural",
            unit: ln.unit || undefined,
          })),
        );
      } else {
        setBomLines([]);
      }
      setDetailBusy(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [unit.id, unit.plant_id, unit.recipe_id, unit.ops_deal_id]);

  async function submitIssue() {
    setError(null);
    const qty = parseFloat(issQty);
    if (!issMat) {
      setError("Pick a material");
      return;
    }
    if (!qty || qty <= 0) {
      setError("Enter a quantity");
      return;
    }
    setIssBusy(true);
    const res = await createManufacturingMaterialIssuance({
      plant_id: unit.plant_id,
      material_id: issMat,
      quantity: qty,
      mfg_unit_id: unit.id,
      deal_id: unit.ops_deal_id ?? undefined,
    });
    setIssBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setIssQty("");
    setIssMat("");
    const issRes = await listManufacturingMaterialIssuances({ mfg_unit_id: unit.id });
    if (issRes.ok) setIssuances(issRes.data.events);
    onIssued?.();
  }

  const plantMats = materials.filter((m) => m.is_active !== false);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col overflow-y-auto border-l border-gray-200 bg-white shadow-xl dark:border-zinc-700 dark:bg-zinc-950">
        <div className="sticky top-0 flex items-start gap-2 border-b border-gray-200 bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-950">
          <div className="min-w-0">
            <div className="font-mono text-lg font-bold text-gray-900 dark:text-zinc-50">
              {unit.display_label}
            </div>
            <div className="text-xs text-gray-500 dark:text-zinc-400">
              {SALE_LABEL[unit.sale_path] || unit.sale_path}
              {unit.ops_deal_id ? ` · deal ${unit.ops_deal_id.slice(0, 8)}…` : " · no deal"}
              {builtStatus ? ` · built ${builtStatus}` : null}
            </div>
          </div>
          <button type="button" className={`${ghostBtnClass} ml-auto`} onClick={onClose}>
            Close
          </button>
        </div>

        <div className="flex flex-col gap-5 p-4">
          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
              {error}
            </p>
          ) : null}

          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Move phase
            </h3>
            {!canMove ? (
              <p className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
                View only — your roles cannot move this unit out of{" "}
                {fromPhase?.display_name ?? "its phase"}.
              </p>
            ) : (
              <>
                <textarea
                  className={`${inputClass} mb-2 w-full`}
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional transition note"
                />
                <div className="flex gap-2">
                  {prev && canPrev ? (
                    <button
                      type="button"
                      className={`${mfgSecondaryBtnClass} flex-1`}
                      disabled={moveBusy}
                      onClick={() => void onMove(unit.id, prev.id, notes).then(onClose)}
                    >
                      ← {prev.display_name}
                    </button>
                  ) : null}
                  {next && canNext ? (
                    <button
                      type="button"
                      className={`${mfgPrimaryBtnClass} flex-1`}
                      disabled={moveBusy}
                      onClick={() => void onMove(unit.id, next.id, notes).then(onClose)}
                    >
                      {next.display_name} →
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </section>

          <BuildPipelineUnitLoadAssign
            loadId={unit.production_load_id}
            openLoads={openLoads}
            busy={loadBusy}
            onAssign={onAssignLoad}
          />

          <BuildPipelineUnitPlantPacket packet={plantPacket} busy={detailBusy} />

          {onShip ? (
            <BuildPipelineUnitShipSection
              unit={unit}
              phases={phases}
              busy={Boolean(shipBusy)}
              onShip={onShip}
            />
          ) : null}

          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Bill of materials
            </h3>
            {detailBusy ? (
              <p className="text-xs text-gray-500">Loading…</p>
            ) : bomLines.length === 0 ? (
              <p className="text-xs text-gray-500">No recipe lines on this unit.</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-zinc-800 dark:border-zinc-700">
                {bomLines.map((ln) => (
                  <li key={ln.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                    <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-zinc-100">
                      {ln.label}
                    </span>
                    <span className="font-mono text-gray-500">
                      ×{ln.quantity}
                      {ln.unit ? ` ${ln.unit}` : ""}
                    </span>
                    <span className="rounded border border-gray-200 px-1 text-[10px] uppercase text-gray-500 dark:border-zinc-600">
                      {ln.role}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Issue material
            </h3>
            <div className="flex flex-wrap items-end gap-2">
              <label className="min-w-[10rem] flex-1 text-xs">
                <span className="text-gray-500">Material</span>
                <select
                  className={`${inputClass} mt-0.5 w-full`}
                  value={issMat}
                  onChange={(e) => setIssMat(e.target.value)}
                >
                  <option value="">Select…</option>
                  {plantMats.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.code} · {m.on_hand_qty} {m.unit} on hand
                    </option>
                  ))}
                </select>
              </label>
              <label className="w-20 text-xs">
                <span className="text-gray-500">Qty</span>
                <input
                  type="number"
                  className={`${inputClass} mt-0.5 w-full`}
                  value={issQty}
                  onChange={(e) => setIssQty(e.target.value)}
                  min={0}
                />
              </label>
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={issBusy}
                onClick={() => void submitIssue()}
              >
                Issue
              </button>
            </div>
            {issuances.length > 0 ? (
              <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-zinc-300">
                {issuances.slice(0, 8).map((ev) => {
                  const mat = materials.find((m) => m.id === ev.material_id);
                  return (
                    <li key={ev.id} className="flex justify-between gap-2">
                      <span>{mat?.name || ev.material_id.slice(0, 8)}</span>
                      <span className="font-mono">
                        {ev.quantity} {mat?.unit || ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>

          <BuildPipelineUnitPhaseHistorySection history={history} phaseById={phaseById} />
          <BuildPipelineUnitProposedFloorSection />
        </div>
      </aside>
    </>
  );
}
